import { PowerAmounts } from "components/detailPane/BusDetailPane/BusDetailPane";
import { CapacityThresholds } from "types/busFilterTypes";
import {
    GeneratorType,
    LOAD_TYPE,
    SOLAR_TYPE,
    STORAGE_TYPE,
    WIND_TYPE
} from "types/generatorType";

// These colors come from Blueprint 3
export const GOOD_COLOR = "#3DCC91"; // green
export const FAIR_COLOR = "#FFB366"; // orange
export const POOR_COLOR = "#FF7373"; // red
export const GREY_COLOR = "#8A9BA8";

// Colorblind-friendly colors
export const GOOD_COLOR_COLORBLIND = "#648fff"; // blue
export const FAIR_COLOR_COLORBLIND = "#ffb000"; // orange
export const POOR_COLOR_COLORBLIND = "#dc267f"; // magenta

const CAPACITY_COLOR_TYPE = [
    GOOD_COLOR,
    FAIR_COLOR,
    POOR_COLOR,
    GOOD_COLOR_COLORBLIND,
    FAIR_COLOR_COLORBLIND,
    POOR_COLOR_COLORBLIND
] as const;
type CapacityColorType = (typeof CAPACITY_COLOR_TYPE)[number];

export const RED_EXPORT_COLOR = "RED";
export const YELLOW_EXPORT_COLOR = "YELLOW";
export const GREEN_EXPORT_COLOR = "GREEN";
const EXPORT_COLOR_TYPE = [
    RED_EXPORT_COLOR,
    YELLOW_EXPORT_COLOR,
    GREEN_EXPORT_COLOR
] as const;
export type ExportColorType = (typeof EXPORT_COLOR_TYPE)[number];

type ColorHexAndString = {
    colorHex: CapacityColorType;
    colorString: ExportColorType;
};

type SizeCalculator = (powerAmounts: PowerAmounts) => number;

const DEFAULT_SIZE_CALCULATOR = (powerAmounts: PowerAmounts): number => {
    return powerAmounts.energySize;
};

const STORAGE_SIZE_CALCULATOR = (powerAmounts: PowerAmounts): number => {
    return Math.min(powerAmounts.energySize, powerAmounts.chargingSize);
};

export const GENERATOR_TYPE_TO_AVAILABILITY_SIZE_TO_USE: {
    [key in GeneratorType]: SizeCalculator;
} = {
    [SOLAR_TYPE]: DEFAULT_SIZE_CALCULATOR,
    [WIND_TYPE]: DEFAULT_SIZE_CALCULATOR,
    [STORAGE_TYPE]: STORAGE_SIZE_CALCULATOR,
    [LOAD_TYPE]: DEFAULT_SIZE_CALCULATOR
};

// Helper function to get the appropriate colors based on colorblind mode
export const getColors = (
    isColorblindMode: boolean
): {
    good: CapacityColorType;
    fair: CapacityColorType;
    poor: CapacityColorType;
} => {
    return {
        good: isColorblindMode ? GOOD_COLOR_COLORBLIND : GOOD_COLOR,
        fair: isColorblindMode ? FAIR_COLOR_COLORBLIND : FAIR_COLOR,
        poor: isColorblindMode ? POOR_COLOR_COLORBLIND : POOR_COLOR
    };
};

export const getCapacityColor = (
    powerAmounts: PowerAmounts,
    capacityThresholds: CapacityThresholds,
    generatorType: GeneratorType,
    isColorblindMode: boolean
): CapacityColorType => {
    const colorHexAndString = getCapacityHexAndString(
        powerAmounts,
        capacityThresholds,
        generatorType,
        isColorblindMode
    );
    return colorHexAndString.colorHex;
};

export const getCapacityColorString = (
    powerAmounts: PowerAmounts,
    capacityThresholds: CapacityThresholds,
    generatorType: GeneratorType,
    isColorblindMode: boolean
): ExportColorType => {
    const colorHexAndString = getCapacityHexAndString(
        powerAmounts,
        capacityThresholds,
        generatorType,
        isColorblindMode
    );
    return colorHexAndString.colorString;
};

// For constraint count: fewer constraints = good, more = poor
const getConstraintCountHexAndString = (
    constraintCount: number,
    thresholds: CapacityThresholds,
    isColorblindMode: boolean
): ColorHexAndString => {
    const colors = getColors(isColorblindMode);

    if (constraintCount < thresholds.mediumThreshold) {
        return {
            colorHex: colors.good,
            colorString: GREEN_EXPORT_COLOR
        };
    } else if (constraintCount < thresholds.highThreshold) {
        return {
            colorHex: colors.fair,
            colorString: YELLOW_EXPORT_COLOR
        };
    } else {
        return {
            colorHex: colors.poor,
            colorString: RED_EXPORT_COLOR
        };
    }
};

export const getConstraintCountColor = (
    constraintCount: number,
    thresholds: CapacityThresholds,
    isColorblindMode: boolean
): CapacityColorType => {
    const colorHexAndString = getConstraintCountHexAndString(
        constraintCount,
        thresholds,
        isColorblindMode
    );
    return colorHexAndString.colorHex;
};

export const getConstraintCountColorString = (
    constraintCount: number,
    thresholds: CapacityThresholds,
    isColorblindMode: boolean
): ExportColorType => {
    const colorHexAndString = getConstraintCountHexAndString(
        constraintCount,
        thresholds,
        isColorblindMode
    );
    return colorHexAndString.colorString;
};

// This function must stay in sync with "getMapboxColorSteps" below.
// For capacity: more capacity = good, less = poor
const getCapacityHexAndString = (
    powerAmounts: PowerAmounts,
    capacityThresholds: CapacityThresholds,
    generatorType: GeneratorType,
    isColorblindMode: boolean
): ColorHexAndString => {
    const colors = getColors(isColorblindMode);
    const sizeCalculatorFunction =
        GENERATOR_TYPE_TO_AVAILABILITY_SIZE_TO_USE[generatorType];

    const size = sizeCalculatorFunction(powerAmounts);

    if (size < capacityThresholds.mediumThreshold) {
        return {
            colorHex: colors.poor,
            colorString: RED_EXPORT_COLOR
        };
    } else if (size < capacityThresholds.highThreshold) {
        return {
            colorHex: colors.fair,
            colorString: YELLOW_EXPORT_COLOR
        };
    } else {
        return {
            colorHex: colors.good,
            colorString: GREEN_EXPORT_COLOR
        };
    }
};

export const isHighAvailableCapacityBus = ({
    powerAmounts,
    capacityThresholds,
    generatorType
}: {
    powerAmounts: PowerAmounts;
    capacityThresholds: CapacityThresholds;
    generatorType: GeneratorType;
}): boolean => {
    const sizeCalculatorFunction =
        GENERATOR_TYPE_TO_AVAILABILITY_SIZE_TO_USE[generatorType];

    return (
        sizeCalculatorFunction(powerAmounts) >= capacityThresholds.highThreshold
    );
};

export const isLowConstraintCountBus = ({
    constraintCount,
    constraintCountThresholds
}: {
    constraintCount: number;
    constraintCountThresholds: CapacityThresholds;
}): boolean => {
    return constraintCount < constraintCountThresholds.mediumThreshold;
};

// This function must stay in sync with "getCapacityHexAndString" above.
// Mapbox doesnt allow for "ties" in their steps. So this logic is a little weird.
// If the threshold for "good" is == the medium threshold (it should be impossible for it to be <)
// then that means there should be no fair section, so we remove that step.
// For capacity: higher values = good (green)
export const getMapboxColorStepsForCapacity = (
    capacityThresholds: CapacityThresholds,
    isColorblindMode: boolean
): (string | number)[] => {
    const colors = getColors(isColorblindMode);
    const steps: (string | number)[] = [
        capacityThresholds.highThreshold,
        colors.good
    ];

    if (capacityThresholds.highThreshold > capacityThresholds.mediumThreshold) {
        steps.unshift(capacityThresholds.mediumThreshold, colors.fair);
    }

    steps.unshift(colors.poor);

    return steps;
};

// For constraint count: lower values = good (green)
export const getMapboxColorStepsForConstraintCount = (
    thresholds: CapacityThresholds,
    isColorblindMode: boolean
): (string | number)[] => {
    const colors = getColors(isColorblindMode);
    const steps: (string | number)[] = [thresholds.highThreshold, colors.poor];

    if (thresholds.highThreshold > thresholds.mediumThreshold) {
        steps.unshift(thresholds.mediumThreshold, colors.fair);
    }

    steps.unshift(colors.good);

    return steps;
};
